import classNames from 'classnames';
import React from 'react';
import * as styles from './KeyboardShortcutsPanel.module.scss';
import Shortcut from './Shortcut';
import RightPanel from '../../app/layout/RightPanel';
import useIsPublicDocumentationView from '../../public/useIsPublicDocumentationView';
import useIsPublicView from '../../utils/useIsPublicView';

interface ShortcutSectionProps {
  children: React.ReactNode;
  title: string;
}

const ShortcutSection = ({ children, title }: ShortcutSectionProps) => {
  return (
    <div className={styles.sectionContainer}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      <ul className={styles.shortcutsList}>{children}</ul>
    </div>
  );
};

interface ShortcutRowProps {
  children?: React.ReactNode;
  name: React.ReactNode;
  keys?: string;
}

const ShortcutRow = ({ children, name, keys }: ShortcutRowProps) => (
  <li className={styles.rowContainer}>
    <span className={styles.rowName}>{name}</span>
    <span>{children || <Shortcut keys={keys} />}</span>
  </li>
);

interface SeparatorProps {
  children: React.ReactNode;
  muted?: boolean;
  mono?: boolean;
  small?: boolean;
}

const ShortcutSeparator = ({
  children,
  muted,
  mono,
  small,
}: SeparatorProps) => (
  <span
    className={classNames(styles.separator, {
      [styles.muted]: muted,
      [styles.small]: small,
      [styles.mono]: mono,
    })}
  >
    {children}
  </span>
);

const KeyboardShortcutsPanel = () => {
  const isPublicView = useIsPublicView();
  const isPublicDocumentationView = useIsPublicDocumentationView();

  const arrow = (
    <ShortcutSeparator muted mono>
      →
    </ShortcutSeparator>
  );

  return (
    <RightPanel.Inner
      header={<RightPanel.NewHeader title="Keyboard shortcuts" />}
    >
      <ShortcutSection title="General">
        <ShortcutRow name="Show/hide this panel" keys="mod+/" />
        {!isPublicView && (
          <>
            <ShortcutRow name="Show/hide AI Copilot" keys="mod+option+b" />
            <ShortcutRow name="Open post preview" keys="mod+option+click" />
            <ShortcutRow name="Close modal" keys="esc" />
            <ShortcutRow name="Create new post" keys="shift+n" />
          </>
        )}
        <ShortcutRow name="Focus search bar" keys="/" />
      </ShortcutSection>
      {!isPublicDocumentationView && (
        <>
          <ShortcutSection title="Editor">
            {!isPublicView && (
              <ShortcutRow name="Enter edit mode">
                <Shortcut keys="e" />
                <ShortcutSeparator muted>or</ShortcutSeparator>
                <Shortcut keys="i" />
              </ShortcutRow>
            )}
            <ShortcutRow name="Exit edit mode" keys="mod+enter" />
            <ShortcutRow name="Undo" keys="mod+z" />
            <ShortcutRow name="Redo" keys="mod+shift+z" />
            <ShortcutRow name="Copy" keys="mod+c" />
            <ShortcutRow name="Copy as markdown" keys="mod+shift+c" />
            <ShortcutRow name="Paste" keys="mod+v" />
            <ShortcutRow name="Paste as plain text" keys="mod+shift+v" />
            <ShortcutRow name="Add comment">
              <Shortcut keys="mod+option+m" />
              <ShortcutSeparator muted>or</ShortcutSeparator>
              <Shortcut keys="a" />
            </ShortcutRow>
            <ShortcutRow name="Create comment" keys="mod+enter" />
            <ShortcutRow name="Select all text or code" keys="mod+a" />
            <ShortcutRow name="Show slash commands" keys="/" />
            <ShortcutRow name="Mention user or group" keys="@" />
            <ShortcutRow name="Mention post" keys="++" />
            <ShortcutRow name="Show emoji picker" keys=":" />
          </ShortcutSection>
          <ShortcutSection title="Formatting">
            <ShortcutRow name="Remove formatting" keys="mod+option+0" />
            <ShortcutRow name="Heading 1" keys="mod+option+1" />
            <ShortcutRow name="Heading 2" keys="mod+option+2" />
            <ShortcutRow name="Ordered list" keys="mod+shift+7" />
            <ShortcutRow name="Bullet list" keys="mod+shift+8" />
            <ShortcutRow name="Check list" keys="mod+shift+9" />
            <ShortcutRow name="Bold" keys="mod+b" />
            <ShortcutRow name="Italic" keys="mod+i" />
            <ShortcutRow name="Strikethrough" keys="mod+option+x" />
            <ShortcutRow name="Link" keys="mod+k" />
          </ShortcutSection>
          <ShortcutSection title="Markdown">
            <ShortcutRow name="Heading 1">
              <Shortcut keys="#" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Heading 2">
              <Shortcut keys="#+#" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Ordered list">
              <Shortcut keys="1+." small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Bullet list">
              <Shortcut keys="-" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Check list">
              <Shortcut keys="[+]" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Code block">
              <Shortcut keys="`+`+`" small />
              {arrow}
              <Shortcut keys="enter" small />
            </ShortcutRow>
            <ShortcutRow name="Blockquote">
              <Shortcut keys=">" small />
              {arrow}
              <Shortcut keys="Space" small />
            </ShortcutRow>
            <ShortcutRow name="Horizontal Rule">
              <Shortcut keys="---" small />
            </ShortcutRow>
            <ShortcutRow name="Bold">
              <Shortcut keys="*+*" small />
              <ShortcutSeparator small>text</ShortcutSeparator>
              <Shortcut keys="*+*" small />
            </ShortcutRow>
            <ShortcutRow name="Italic">
              <Shortcut keys="*" small />
              <ShortcutSeparator small>text</ShortcutSeparator>
              <Shortcut keys="*" small />
            </ShortcutRow>
            <ShortcutRow name="Strikethough">
              <Shortcut keys="~+~" small />
              <ShortcutSeparator small>text</ShortcutSeparator>
              <Shortcut keys="~+~" small />
            </ShortcutRow>
            <ShortcutRow name="Inline code">
              <Shortcut keys="`" small />
              <ShortcutSeparator small>code</ShortcutSeparator>
              <Shortcut keys="`" small />
            </ShortcutRow>
          </ShortcutSection>
        </>
      )}
    </RightPanel.Inner>
  );
};

export default KeyboardShortcutsPanel;
